import { useState } from "react";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TermsCheckbox } from "@/components/ui/TermsCheckbox";
import { UsernameInput } from "@/components/ui/UsernameInput";
import { useToast } from "@/hooks/use-toast";
import { createUserSafe } from '@/api/users';
import { UserRole } from '@/lib/schema';
import { Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { getAuthErrorMessage } from "@/lib/authUtils";

interface RegisterFormProps {
  role: UserRole;
  onSuccess: () => void;
}

export function RegisterForm({ role, onSuccess }: RegisterFormProps) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isUsernameValid, setIsUsernameValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { signInWithGoogle } = useAuth();

  const handleEmailRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!acceptedTerms) {
      toast({
        title: "Terms and Conditions Required",
        description: "Please accept the Terms and Conditions to continue.",
        variant: "destructive"
      });
      return;
    }
    
    if (!isUsernameValid) {
      toast({
        title: "Username Validation Required",
        description: "Please validate your username before continuing.",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUid = userCredential.user.uid;
      
      const userData = {
        email,
        username,
        firstName,
        lastName,
        role: role as UserRole,
        passwordResetRequired: false,
        onboardingCompleted: true,
      };
      
      await createUserSafe(firebaseUid, userData);
      
      // Wait a moment for the auth state to propagate
      await new Promise(resolve => setTimeout(resolve, 500));
      
      toast({
        title: "Account created!",
        description: "Welcome to AiR-Project. You can now start using the platform.",
      });
      onSuccess();
    } catch (error: any) {
      toast({
        title: "Registration failed",
        description: getAuthErrorMessage(error),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    setLoading(true);
    try {
      await signInWithGoogle(onSuccess, role);
    } catch (error: any) {
      toast({
        title: "Registration failed",
        description: getAuthErrorMessage(error),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getRoleDisplayName = (role: UserRole) => {
    switch (role) {
      case UserRole.USER:
        return "Job Seeker";
      case UserRole.RECRUITER:
        return "Recruiter";
      case UserRole.SUPER_ADMIN:
        return "Super Administrator";
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleEmailRegister} className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName" className="text-slate-700 font-medium space-grotesk">First Name</Label>
            <Input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              disabled={loading}
              autoComplete="given-name"
              className="h-12 border-slate-200 focus:border-orange-400 focus:ring-orange-400/20 focus:ring-2 focus:outline-none focus-visible:ring-orange-400/20 focus-visible:ring-2 focus-visible:outline-none rounded-lg space-grotesk bg-white/80 backdrop-blur-sm transition-all"
              placeholder="John"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName" className="text-slate-700 font-medium space-grotesk">Last Name</Label>
            <Input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              disabled={loading}
              autoComplete="family-name"
              className="h-12 border-slate-200 focus:border-orange-400 focus:ring-orange-400/20 focus:ring-2 focus:outline-none focus-visible:ring-orange-400/20 focus-visible:ring-2 focus-visible:outline-none rounded-lg space-grotesk bg-white/80 backdrop-blur-sm transition-all"
              placeholder="Doe"
            />
          </div>
        </div>
        <UsernameInput
          value={username}
          onChange={setUsername}
          disabled={loading}
          required
          label="Username"
          placeholder="johndoe"
          onValidationChange={setIsUsernameValid}
        />
        <div className="space-y-2">
          <Label htmlFor="email" className="text-slate-700 font-medium space-grotesk">Email</Label>
            <Input
              id="email"
              type="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              autoComplete="email"
              className="h-12 border-slate-200 focus:border-orange-400 focus:ring-orange-400/20 focus:ring-2 focus:outline-none focus-visible:ring-orange-400/20 focus-visible:ring-2 focus-visible:outline-none rounded-lg space-grotesk bg-white/80 backdrop-blur-sm transition-all"
              placeholder="john.doe@example.com"
            />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-slate-700 font-medium space-grotesk">Password</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            disabled={loading}
            autoComplete="new-password"
            className="h-12 border-slate-200 focus:border-orange-400 focus:ring-orange-400/20 focus:ring-2 focus:outline-none focus-visible:ring-orange-400/20 focus-visible:ring-2 focus-visible:outline-none rounded-lg space-grotesk bg-white/80 backdrop-blur-sm transition-all"
            placeholder="Minimum 6 characters"
          />
        </div>
        <TermsCheckbox
          checked={acceptedTerms}
          onCheckedChange={setAcceptedTerms}
          disabled={loading}
          className="pt-2"
        />
        <Button 
          type="submit" 
          variant="primary"
          size="primary"
          className="w-full h-12"
          disabled={loading || !acceptedTerms || !isUsernameValid}
        >
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create Account
        </Button>
      </form>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-slate-200" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-gradient-to-b from-white to-slate-50/50 px-3 text-slate-500 space-grotesk font-medium tracking-wider">Or continue with</span>
        </div>
      </div>

      <Button
        type="button"
        variant="secondary"
        size="primary"
        className="w-full h-12"
        onClick={handleGoogleRegister}
        disabled={loading}
      >
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Sign up with Google
      </Button>
    </div>
  );
}
